/**
 * DEMO DATA — the single seam where simulated data enters the dashboard, and
 * the driver for the page's live channel.
 *
 * ⚠️  PHASE 1 (CLIENT DEMO) ONLY. Real data arrives next phase. See ../demo-mode.ts.
 *
 * With `DEMO_MODE` off this still runs, but purely as the live-store driver
 * republishing the REAL socket feed — so flipping the flag remains the only
 * step needed to go live, and the render architecture is identical either way.
 *
 * RENDER BUDGET — the point of this file's shape
 * ----------------------------------------------
 * Two separate cadences, deliberately kept apart:
 *
 *   LIVE (1s)     agents, calls and the shared clock. Pushed into
 *                 `../live-store.ts` and consumed ONLY by the live panels.
 *                 Never returned from this hook, because returning it would
 *                 re-render the page — and with it `WidthProvider` and all ten
 *                 react-grid-layout items — once a second.
 *
 *   HISTORY (5s)  the aggregate tables. Returned normally, so the page renders
 *                 roughly every five seconds instead of every one.
 *
 * The earlier version returned live data and drove a page-level tick. Panel
 * memoisation could not save it: the grid sits above the panels, so it had
 * already re-laid-out ten items before memo got a say.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SupervisorAgentSummary, SupervisorLiveCall } from '@kc3/types'
import { DEMO_MODE } from '../demo-mode.js'
import { publishLive } from '../live-store.js'
import type { CampaignSummaryResponse, CampaignOption } from '../types.js'
import {
  applyDemoAgentAction,
  createDemoState,
  tickDemo,
  toAgentSummaries,
  toLiveCalls,
  type DemoState,
  type RosterEntry,
} from './simulator.js'
import { buildDemoSummary, DEMO_CAMPAIGN_OPTIONS } from './aggregate.js'

/** Cadence of the live channel — call timers advance at this rate. */
const LIVE_INTERVAL_MS = 1000

/**
 * Cadence of the historical rollups.
 *
 * These summarise a 12-hour window, so a few new calls move them by a fraction
 * of a percent — far below perception — while each refresh re-renders the page
 * and therefore the whole grid. Five seconds keeps them visibly alive at a
 * fifth of the structural cost.
 */
const HISTORY_INTERVAL_MS = 5000

export interface DemoOverlayInput {
  /** Agents from the real socket feed — the demo roster when present. */
  realAgents: SupervisorAgentSummary[]
  realCalls: SupervisorLiveCall[]
  realHistory: CampaignSummaryResponse
  realCampaigns: CampaignOption[]
  hours: number
  intervalMin: string
  campaignId: string
}

export interface DemoOverlayOutput {
  /** Aggregate tables. Changes on the HISTORY cadence, not the live one. */
  history: CampaignSummaryResponse
  campaigns: CampaignOption[]
  isDemo: boolean
  /** Applies a supervisor agent-action to the simulation. No-op when live. */
  applyAgentAction: (action: 'force_pause' | 'force_logout', userId: string) => void
}

export function useDemoOverlay(input: DemoOverlayInput): DemoOverlayOutput {
  const {
    realAgents,
    realCalls,
    realHistory,
    realCampaigns,
    hours,
    intervalMin,
    campaignId,
  } = input

  const stateRef = useRef<DemoState | null>(null)

  // Seed synchronously on first render. Doing it in an effect would leave one
  // frame with no state, rendering the empty message and then replacing it — a
  // visible flash every time the page opens.
  if (DEMO_MODE && stateRef.current === null) {
    stateRef.current = createDemoState([], Date.now())
  }

  /* ── Live channel (1s, never re-renders the page) ─────────────────────── */

  // The real feed is read through a ref so the publishing interval can see the
  // latest socket data without this hook needing to re-run — and therefore
  // without the page re-rendering — on every push.
  const realRef = useRef({ agents: realAgents, calls: realCalls })
  realRef.current = { agents: realAgents, calls: realCalls }

  useEffect(() => {
    const publish = (): void => {
      const now = Date.now()
      const state = stateRef.current
      if (DEMO_MODE && state) {
        tickDemo(state, now)
        publishLive({
          agents: toAgentSummaries(state),
          calls: toLiveCalls(state, now),
        })
        return
      }
      const real = realRef.current
      publishLive({ agents: real.agents, calls: real.calls })
    }

    publish() // paint immediately rather than after a blank first second
    const id = setInterval(publish, LIVE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  /* ── Roster ──────────────────────────────────────────────────────────── */

  const rosterKey = realAgents.map((a) => a.id).sort().join(',')

  useEffect(() => {
    if (!DEMO_MODE) return
    // Only rebuild once a REAL roster arrives; the placeholder seed above
    // covers the empty case, and rebuilding for it would restart every counter
    // the client is watching climb.
    if (realAgents.length === 0) return

    const roster: RosterEntry[] = realAgents.map((a) => ({
      id: a.id,
      userId: a.userId,
      name: a.name,
      ...(a.email ? { email: a.email } : {}),
      ...(a.extension ? { extension: a.extension } : {}),
    }))
    stateRef.current = createDemoState(roster, Date.now())
    // eslint-disable-next-line
  }, [rosterKey])

  /* ── History channel (5s, re-renders the page) ────────────────────────── */

  const [historyTick, setHistoryTick] = useState(0)

  useEffect(() => {
    if (!DEMO_MODE) return
    const id = setInterval(() => setHistoryTick((n) => n + 1), HISTORY_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  const state = stateRef.current
  const active = DEMO_MODE && state !== null

  // Recomputed only when the page renders, which is now the history cadence,
  // a filter change, or a layout edit — not once a second.
  const history = active
    ? buildDemoSummary(state, Date.now(), hours, Number(intervalMin), campaignId || null)
    : realHistory

  // historyTick is read purely to tie the value above to the 5s interval;
  // without the reference the linter would call it unused.
  void historyTick

  const applyAgentAction = useCallback(
    (action: 'force_pause' | 'force_logout', userId: string) => {
      const s = stateRef.current
      if (!DEMO_MODE || !s) return
      applyDemoAgentAction(s, action, userId, Date.now())
    },
    [],
  )

  return {
    history,
    campaigns: active ? DEMO_CAMPAIGN_OPTIONS : realCampaigns,
    isDemo: active,
    applyAgentAction,
  }
}
